import './SourcePromptsTable.scss';
import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Skeleton } from 'primereact/skeleton';
import { useTranslation } from 'react-i18next';
import { SearchFilter } from '../../../components/search-filter/SearchFilter';
import EmptyPanel from '../../../../../shared/components/empty-panel/EmptyPanel';
import { PROJECTS, QUESTIONS } from '../../../../../shared/routes/path';

interface PromptRow {
  keyword: string;
  monitorPromptId: number;
  createdAt: string;
  snippet: string;
  queriesCount: number;
}

interface SourcePromptsTableProps {
  prompts: PromptRow[];
  isLoading: boolean;
  projectId: string;
}

const SourcePromptsTable: React.FC<SourcePromptsTableProps> = ({ prompts, isLoading, projectId }) => {
  const { t } = useTranslation();
  const [keyword, setKeyword] = useState('');

  const filteredPrompts = useMemo(() => {
    if (!keyword.trim()) return prompts;
    const lower = keyword.toLowerCase();
    return prompts.filter((p) => p.keyword.toLowerCase().includes(lower));
  }, [prompts, keyword]);

  return (
    <div className="stats__panel source-prompts-table">
      <div className="page-box__title-action">
        <div className="page-box__title">{t('sourcePromptsTitle')}</div>
        <div className="page-box__source-filter">
          <SearchFilter value={keyword} onChange={setKeyword} className="search-filter--inline" />
        </div>
      </div>

      {isLoading ? (
        <Skeleton className="skeleton--table">
          <span />
          <span />
          <span />
          <span />
          <span />
          <span />
          <span />
        </Skeleton>
      ) : (
        <DataTable
          value={filteredPrompts}
          stripedRows
          tableClassName="table"
          className="table-wrapper"
          tableStyle={{ width: '100%' }}
          paginator
          emptyMessage={() => <EmptyPanel />}
          paginatorClassName="pagination"
          paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
          rows={10}
          rowsPerPageOptions={[10, 25, 50]}
          dataKey="keyword"
          currentPageReportTemplate={t('rowsNumber') + ':'}
        >
          <Column
            field="keyword"
            header={t('sourcePromptsColumn')}
            headerClassName="table__header"
            style={{ minWidth: '300px' }}
            body={(rowData: PromptRow) => (
              <Link
                to={`${PROJECTS}/${projectId}${QUESTIONS}/${rowData.monitorPromptId}/daily/all`}
                className="source-prompts-table__keyword text-ellipsis"
              >
                {rowData.keyword}
              </Link>
            )}
          />
          <Column
            field="queriesCount"
            header={t('sourceQueriesColumn')}
            headerClassName="table__header table__header--center"
            bodyClassName="table__cell--center"
            style={{ width: '120px', minWidth: '120px' }}
            sortable
            body={(rowData: PromptRow) => rowData.queriesCount}
          />
        </DataTable>
      )}
    </div>
  );
};

export default SourcePromptsTable;
